/**
 * Empty-state artwork.
 *
 * This module used to hold eleven hand-authored "scenes" — flat food resting on
 * a drawn shelf, 43 `d="M…"` strings written without ever seeing them render.
 * Several were visibly wrong, and the ones that were not were wrong in ways
 * nobody had checked. Path geometry is no longer authored here, or anywhere
 * else in this repository.
 *
 * What replaced them is deliberately more modest: **one Phosphor glyph, drawn
 * large**. An empty state has one job — say "there is nothing here yet, and
 * here is the kind of thing that belongs here" — and a single accurate object
 * says it better than an inaccurate tableau. The scene grammar (shared shelf,
 * one ink weight, no mascot) is gone with the scenes, because it was a grammar
 * for drawings this project should not be making.
 *
 * The route resolution below is unchanged and is the part worth keeping:
 * `illustrationForPath()` goes through `groupForPath()`, the same reverse
 * lookup the sidebar uses, so a screen added to the registry inherits artwork
 * with no per-page edit.
 */

import { groupForPath } from './catalog-items.js';
import { buildIcon } from './brand-icons.js';
import { ICON_BODIES } from './icon-bodies.generated.js';

/** Every registered id, in the order screens were adopted. */
export const ILLUSTRATION_IDS = [
  'empty-inventory',
  'empty-shopping',
  'empty-catalog',
  'empty-locations',
  'empty-units',
  'empty-users',
  'empty-product-masters',
  'empty-recipes',
  'empty-history',
  'empty-reminders',
  'empty-ingredients',
];

const FALLBACK_ID = 'empty-inventory';

/** Explicit route → artwork. Anything not listed falls back to its group. */
const BY_PATH = {
  '/': 'empty-inventory',
  '/shopping': 'empty-shopping',
  '/catalog': 'empty-catalog',
  '/locations': 'empty-locations',
  '/units': 'empty-units',
  '/users': 'empty-users',
  '/product-masters': 'empty-product-masters',
  '/recipes': 'empty-recipes',
  '/history': 'empty-history',
  '/reminders': 'empty-reminders',
  '/ingredients': 'empty-ingredients',
};

/** Group → artwork, so a new screen inherits something sensible with no wiring. */
const BY_GROUP = {
  vorrat: 'empty-inventory',
  kueche: 'empty-recipes',
  auswertung: 'empty-history',
  verwaltung: 'empty-product-masters',
};

/**
 * Which artwork belongs to a route.
 *
 * @param {string|null|undefined} path
 * @returns {string}
 */
export function illustrationForPath(path) {
  if (typeof path === 'string' && BY_PATH[path]) return BY_PATH[path];
  const group = groupForPath(path);
  return (group && BY_GROUP[group]) || FALLBACK_ID;
}

/**
 * Build one empty-state illustration.
 *
 * An unknown id resolves to the fallback rather than throwing, because an empty
 * state that fails to say "there is nothing here yet" is worse than a slightly
 * wrong picture. The cost is that a mistyped id renders the *wrong* artwork
 * silently — which is exactly what happened when `/shopping` asked for
 * `shopping-empty` and drew the inventory glyph for its whole life. The static
 * guard in `web/test/illustration.test.mjs` asserts every `illustrationId:`
 * literal in `web/src` is registered; it can only be caught there, precisely
 * because the runtime fallback is intentional.
 *
 * @param {{ id?: string, width?: number, height?: number }} [opts]
 * @returns {SVGSVGElement}
 */
export function illustration({ id, width = 96, height = 96 } = {}) {
  const resolved = Object.prototype.hasOwnProperty.call(ICON_BODIES, id) ? id : FALLBACK_ID;
  const svg = buildIcon(resolved);
  // Override the chrome's default h-6/w-6: this is artwork, not an inline icon.
  svg.setAttribute('class', 'illustration-glyph');
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.dataset.illustration = resolved;
  return svg;
}
